import { Box, Button, InputAdornment, ListSubheader, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useMemo, useState } from "react";
import type { InvoiceDiscountApplyToOption, InvoiceDiscountDraft } from "./InvoiceDiscountsCard";

interface DiscountModalFormProps {
  initialValues?: InvoiceDiscountDraft;
  applyToOptions?: InvoiceDiscountApplyToOption[];
  onSave: (data: Omit<InvoiceDiscountDraft, "id">) => void;
  onCancel: () => void;
  isEdit?: boolean;
}

export function DiscountModalForm({ initialValues, applyToOptions = [], onSave, onCancel, isEdit = false }: DiscountModalFormProps) {
  const [description, setDescription] = useState(initialValues?.description ?? "");
  const [kind, setKind] = useState<"amount" | "percentage">(initialValues?.kind ?? "amount");
  const [value, setValue] = useState(initialValues?.value ?? "");
  const [appliesToKey, setAppliesToKey] = useState(initialValues?.appliesToKey ?? "invoice");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const parsedValue = Number.parseFloat(String(value || "0"));
  const hasValue = Number.isFinite(parsedValue) && parsedValue >= 0 && value.trim().length > 0;
  const hasValidDescription = description.trim().length > 0;

  const descriptionError = hasSubmitted && !hasValidDescription;
  const valueError = hasSubmitted && !hasValue;

  const groupedOptions = useMemo(() => {
    const general: InvoiceDiscountApplyToOption[] = [];
    const services: InvoiceDiscountApplyToOption[] = [];
    const extraServices: InvoiceDiscountApplyToOption[] = [];

    applyToOptions.forEach((opt) => {
      if (opt.type === "invoice" || opt.key === "invoice") {
        general.push(opt);
      } else if (opt.type === "service" || opt.eventUid != null) {
        services.push(opt);
      } else if (opt.type === "extra_line_item" || opt.extraLineItemId != null) {
        extraServices.push(opt);
      } else {
        general.push(opt);
      }
    });

    return { general, services, extraServices };
  }, [applyToOptions]);

  const handleSubmit = (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setHasSubmitted(true);
    if (!hasValidDescription || !hasValue) return;

    const option = applyToOptions.find((opt) => opt.key === appliesToKey);
    onSave({
      description: description.trim(),
      kind,
      value: String(parsedValue),
      appliesToType: option?.type ?? "invoice",
      appliesToKey,
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        p: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxSizing: "border-box",
        gap: 2.5,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField
          size="small"
          label="Discount description / reason"
          placeholder="e.g. Seasonal promotion, Referral discount"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          required
          autoFocus
          error={descriptionError}
          helperText={descriptionError ? "Description is required." : undefined}
        />

        {applyToOptions.length > 1 && (
          <TextField size="small" select label="Applies to" value={appliesToKey} onChange={(e) => setAppliesToKey(e.target.value)} fullWidth>
            {groupedOptions.general.length > 0 && <ListSubheader sx={{ fontWeight: 700, lineHeight: "32px" }}>General</ListSubheader>}
            {groupedOptions.general.map((option) => (
              <MenuItem key={option.key} value={option.key} sx={{ pl: 3 }}>
                {option.label}
              </MenuItem>
            ))}

            {groupedOptions.services.length > 0 && <ListSubheader sx={{ fontWeight: 700, lineHeight: "32px" }}>Main Services / Visits</ListSubheader>}
            {groupedOptions.services.map((option) => (
              <MenuItem key={option.key} value={option.key} sx={{ pl: 3 }}>
                {option.label}
              </MenuItem>
            ))}

            {groupedOptions.extraServices.length > 0 && <ListSubheader sx={{ fontWeight: 700, lineHeight: "32px" }}>Other Services</ListSubheader>}
            {groupedOptions.extraServices.map((option) => (
              <MenuItem key={option.key} value={option.key} sx={{ pl: 3 }}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={kind}
            onChange={(_, val) => {
              if (val) setKind(val);
            }}
            sx={{
              height: 40,
              "& .MuiToggleButton-root": { px: 2, fontSize: "0.875rem", fontWeight: 700 },
            }}
          >
            <ToggleButton value="amount">$</ToggleButton>
            <ToggleButton value="percentage">%</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            size="small"
            label={kind === "percentage" ? "Percentage" : "Amount"}
            type="number"
            placeholder="0.00"
            inputProps={{
              min: 0,
              step: "0.01",
              max: kind === "percentage" ? 100 : undefined,
            }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            fullWidth
            required
            error={valueError}
            helperText={valueError ? "Enter a valid value." : undefined}
            sx={{
              "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
              "& input[type=number]": {
                MozAppearance: "textfield",
              },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">{kind === "percentage" ? "%" : "$"}</InputAdornment>,
            }}
          />
        </Box>
      </Box>

      <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
        <Button variant="outlined" color="inherit" onClick={onCancel} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" onClick={handleSubmit} sx={{ textTransform: "none", fontWeight: 700 }}>
          {isEdit ? "Save changes" : "Add discount"}
        </Button>
      </Stack>
    </Box>
  );
}
